import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCachedOrder } from '../../../../store/slices/OrderSlice';
import { IonSegmentCustomEvent, SegmentChangeEventDetail } from '@ionic/core/dist/types/components';
import { selectOrderMethodModalData, setInitState, setScheduledFor } from '../../../../store/slices/OrderMethodSlice';
import { actionCreators } from '../../../../store/actions';
import useOrderFlow from '../../../../hooks/order/useOrderFlow';
import { StoreInterface } from '../../../../store/types';
import { ORDER_METHODS, REDUX_STORE_SLICES } from '../../../../services/exports/Constants';
import { IModal } from '../../router/ModalRouter';
import useDeliveryOrderMethodValidation from './useDeliveryOrderMethodValidation';
import { Modals } from '../../exports/Enums';
import useOrderSlots from '../../../../hooks/utility/useOrderSlots';
import moment, { Moment } from 'moment/moment';

const useOrderMethodModal = ({ isBack, closeModal, openModal }: Partial<IModal>) => {
  const cachedOrder = useSelector(selectCachedOrder);
  const orderMethodModalData = useSelector(selectOrderMethodModalData);
  const companyState = useSelector((state: StoreInterface) => state[REDUX_STORE_SLICES.INITIAL_DATA].company.state);
  const companyCountry = useSelector((state: StoreInterface) => state[REDUX_STORE_SLICES.INITIAL_DATA].company.country);
  const dispatch = useDispatch();
  const segmentRef = useRef<HTMLIonSegmentElement>(null);
  const { setOrderMethodInfoInfoComplete } = useOrderFlow();
  const { validate, validationError, loading, isAllDeliveryFieldsFilled } = useDeliveryOrderMethodValidation();
  const selectedDate = useMemo<Moment>(() => {
    if (orderMethodModalData.scheduledFor) {
      return moment(orderMethodModalData.scheduledFor);
    }

    return moment();
  }, [orderMethodModalData.scheduledFor, orderMethodModalData.orderMethod, isBack]);
  const { isBusinessTime, getNextBusinessTime, isSlotsLoading } = useOrderSlots({
    orderMethod: orderMethodModalData.orderMethod,
    startDate: selectedDate.format('YYYY-MM'),
    date: selectedDate,
  });
  const isDeliveryAddressValid =
    orderMethodModalData.orderMethod !== ORDER_METHODS.DELIVERY || isAllDeliveryFieldsFilled;
  const isConfirmBtnDisabled = loading || !isDeliveryAddressValid;

  useEffect(() => {
    if (isBack) {
      return;
    }

    dispatch(
      setInitState({
        orderMethod: cachedOrder.method,
        scheduledFor: cachedOrder.scheduled_for,
        deliveryData: {
          delivery_street_name: cachedOrder.delivery_street_name ?? '',
          delivery_street_number: cachedOrder.delivery_street_number ?? '',
          delivery_city: cachedOrder.delivery_city ?? '',
          delivery_zip_code: cachedOrder.delivery_zip_code ?? '',
          delivery_lat: +cachedOrder.delivery_lat,
          delivery_lng: +cachedOrder.delivery_lng,
        },
      }),
    );
  }, [isBack]);

  useEffect(() => {
    if (isSlotsLoading) {
      return;
    }

    if (isBusinessTime(selectedDate.format())) {
      return;
    }

    dispatch(setScheduledFor(getNextBusinessTime(selectedDate.format())));
  }, [selectedDate, isSlotsLoading, orderMethodModalData.orderMethod]);

  const onSegmentChange = (e: IonSegmentCustomEvent<SegmentChangeEventDetail>) => {
    dispatch(setInitState({ orderMethod: e.detail.value }));

    if (e.detail.value === ORDER_METHODS.PICKUP) {
      return;
    }

    if (isAllDeliveryFieldsFilled) {
      return;
    }

    openModal({ id: Modals.DeliveryAddress });
  };
  const handlePickupConfirmed = (): void => {
    dispatch(
      actionCreators.order.updateOrder({
        scheduled_for: orderMethodModalData.scheduledFor,
        method: orderMethodModalData.orderMethod,
      }),
    );
    setOrderMethodInfoInfoComplete();
    closeModal();
  };
  const handleDeliveryConfirmed = async (): Promise<void> => {
    const isValid = await validate(orderMethodModalData.deliveryData);

    if (!isValid) {
      return;
    }

    dispatch(
      actionCreators.order.updateOrder({
        delivery_street_name: orderMethodModalData.deliveryData.delivery_street_name,
        delivery_street_number: orderMethodModalData.deliveryData.delivery_street_number,
        delivery_city: orderMethodModalData.deliveryData.delivery_city,
        delivery_state: companyState,
        delivery_country: companyCountry,
        delivery_zip_code: orderMethodModalData.deliveryData.delivery_zip_code,
        delivery_lat: orderMethodModalData.deliveryData.delivery_lat,
        delivery_lng: orderMethodModalData.deliveryData.delivery_lng,
        scheduled_for: orderMethodModalData.scheduledFor,
        method: orderMethodModalData.orderMethod,
      }),
    );

    setOrderMethodInfoInfoComplete();
    closeModal();
  };
  const onConfirmClick = async (): Promise<void> => {
    if (orderMethodModalData.orderMethod === ORDER_METHODS.PICKUP) {
      return handlePickupConfirmed();
    }

    return handleDeliveryConfirmed();
  };

  return {
    onSegmentChange,
    orderMethod: orderMethodModalData.orderMethod,
    segmentRef,
    onConfirmClick,
    isConfirmBtnDisabled,
    validationError,
  };
};

export default useOrderMethodModal;
